import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import { Button, Col, notification, Popconfirm, Row, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { capitalize, compact, has, isEmpty, orderBy } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { styled } from 'styled-components'
import {
  Can,
  Copyable,
  Icon,
  IconContainer,
  PageContainer,
  PageTitleRow,
  TransactionHistoryModal,
} from '../../components'
import { withConfig } from '../../modules'
import { configPropTypes } from '../../types'
import {
  createDownloadLink,
  handleBackendError,
  transformSearchParams,
} from '../../utils'
import { getCountryList } from '../country-management/api'
import UserConfirmationModal from './UserConfirmationModal'
import UserDetailsModal from './UserDetailsModal'
import {
  confirmUser,
  depersonalizeUser,
  exportUsers,
  getUserList,
  updateUser,
} from './api'

const StyledButton = styled(Button)`
  margin: 5px 0;
  font-size: 11px;
`

export const Users = ({ config }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isModalVisible, setModalVisibility] = useState(false)
  const [isModalLoading, setModalLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState()
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [countries, setCountries] = useState([])
  const [countryFilters, setCountryFilters] = useState([])
  const savedListParams = useRef('')
  const typeFilters = orderBy(
    config.config?.user_types.map((type) => ({ text: type, value: type })),
    'text',
    'ASC',
  )
  const statusFilters = orderBy(
    config.config?.user_statuses.map((status) => ({
      text: status,
      value: status,
    })),
    'text',
    'DESC',
  )
  const consentsFilters = [
    { text: 'Market research consent - Accepted', value: true },
    { text: 'Market research consent - Declined', value: false },
  ]

  const [metaData, setMetaData] = useState()

  useEffect(() => {
    if (isEmpty(searchParams.toString()))
      setSearchParams({ items_per_page: 25, page: 1, sort_by: 'created_at DESC' })
  }, [])

  useEffect(() => {
    getCountryList()
      .then(({ data }) => {
        setCountries(data.data)
        const filters = orderBy(
          data.data.map((country) => ({
            text: country.name,
            value: country.id,
          })),
          'text',
          'ASC',
        )

        setCountryFilters(filters)
      })
      .catch((error) => {
        handleFailure(error, 'Fetching the countries failed!')
      })
  }, [])

  useEffect(() => {
    if (!isEmpty(searchParams.toString()) && !isModalVisible.transactions)
      fetchUserList(searchParams.toString())
  }, [searchParams])

  const fetchUserList = (params) => {
    setIsLoading(true)

    getUserList(params)
      .then(({ data }) => {
        setUsers(data.data)
        setMetaData(data.meta)
        setIsLoading(false)
      })
      .catch((error) => {
        setIsLoading(false)

        handleFailure(error, 'Fetching user list failed!')
      })
  }

  const userCanConfirm = has(
    config.config.user_permissions,
    'user.put.user_id_confirm',
  )

  const tableColumns = compact([
    {
      className: 'userIdSelenium',
      dataIndex: 'id',
      render: (_, { id }) => (
        <div style={{ maxWidth: 100 }}>
          <Copyable text={id} />
        </div>
      ),
      sorter: true,
      title: 'Id',
      ...getTableFilter({ name: 'id' }),
    },
    {
      className: 'userTypeSelenium',
      dataIndex: 'user_type',
      render: (_, record) => (
        <IconContainer>
          {
            <Tooltip title={capitalize(record.user_type)}>
              <Icon cursor="default" type={record.user_type} />
            </Tooltip>
          }
        </IconContainer>
      ),
      title: 'User type',
      ...getTableFilter({
        filterValues: typeFilters,
        isMultiple: false,
        name: 'user_type',
        type: 'checkbox',
      }),
    },
    {
      className: 'userNameSelenium',
      dataIndex: 'name',
      sorter: true,
      title: 'Name',
      ...getTableFilter({ name: 'name' }),
    },
    {
      className: 'emailSelenium',
      dataIndex: 'email',
      render: (_, record) => <Copyable text={record.email} />,
      title: 'Email',
      ...getTableFilter({ name: 'email' }),
    },
    {
      className: 'userCountrySelenium',
      render: (_, record) => <>{record.country_name}</>,
      title: 'Country',
      ...getTableFilter({
        filterValues: countryFilters,
        isMultiple: false,
        name: 'country_id',
        type: 'checkbox',
      }),
    },
    {
      className: 'zipCodeSelenium',
      dataIndex: 'zip_code',
      title: 'Zip code',
      ...getTableFilter({ name: 'zip_code' }),
    },
    {
      className: 'storeSelenium',
      render: (_, record) => <>{record.store_name}</>,
      title: 'Store name',
      ...getTableFilter({ name: 'store_name' }),
    },
    {
      className: 'statusSelenium',
      dataIndex: 'status',
      sorter: true,
      title: 'Status',
      ...getTableFilter({
        filterValues: statusFilters,
        name: 'status[]',
        type: 'checkbox',
      }),
    },
    userCanConfirm
      ? {
        className: 'confirmationOptionsSelenium',
        key: 'confirmationOptions',
        render: (_, record) => (
          <Row>
            {record.status === 'validation_needed' && (
              <StyledButton
                data-testid="confirmBtn"
                name="confirmationButton"
                onClick={() => {
                  handleOpenModal('confirmation', record)
                }}
                style={{
                  background: 'rgba(0,214,143,.08)',
                  borderColor: '#00d68f',
                  color: '#00d68f',
                  width: '100%',
                }}
              >
                CONFIRM
              </StyledButton>
            )}
          </Row>
        ),
        title: 'Confirmation options',
      }
      : null,
    {
      className: 'createdAtSelenium',
      dataIndex: 'created_at',
      render: (dateTime) => <span>{dayjs(dateTime).format('DD.MM.YYYY')}</span>,
      sorter: true,
      title: 'Created at',
      ...getTableFilter({ name: 'created_at', type: 'date' }),
    },
    {
      className: 'numberOfPackagesReturnedSelenium',
      dataIndex: 'number_of_returned_items',
      key: 'number_of_returned_items',
      sorter: true,
      title: 'Number of packages returned',
    },
    {
      className: 'consentsSelenium',
      render: (_, record) => (
        <Row className="icon-wrapper">
          <Tooltip title="Marketing emails">
            <Icon
              cursor="default"
              type={record.marketing_consent ? 'checkCircle' : 'closeCircle'}
            />
          </Tooltip>
          <Tooltip title="Market research consent">
            <Icon
              cursor="default"
              type={
                record.market_research_consent ? 'checkCircle' : 'closeCircle'
              }
            />
          </Tooltip>
          <Tooltip title="Marketing push notification">
            <Icon
              cursor="default"
              type={
                record.marketing_notification_consent
                  ? 'checkCircle'
                  : 'closeCircle'
              }
            />
          </Tooltip>
        </Row>
      ),
      title: 'Consents',
      ...getTableFilter({
        filterValues: consentsFilters,
        isMultiple: false,
        name: 'market_research_consent',
        type: 'checkbox',
      }),
    },
    {
      className: 'balanceSelenium',
      dataIndex: 'balance',
      key: 'balance',
      render: (_, record) => (
        <Can
          no={() =>
            new Intl.NumberFormat(record.country_locales[0], {
              currency: record.currency_symbol,
              style: 'currency',
            }).format(record.balance)
          }
          requiredPermission="user.get.user_id_transactions"
          yes={() => (
            <Tooltip title="Open transaction history">
              <Button
                data-testid="balanceBtnSelenium"
                name="balanceBtnSelenium"
                onClick={() => handleOpenModal('transactions', record)}
                type="link"
              >
                {new Intl.NumberFormat(record.country_locales[0], {
                  currency: record.currency_symbol,
                  style: 'currency',
                }).format(record.balance)}
              </Button>
            </Tooltip>
          )}
        />
      ),
      sorter: true,
      title: 'Balance',
    },
    {
      className: 'userActionSelenium',
      key: 'action',
      render: (_, record) => (
        <IconContainer>
          {record.status !== 'inactive' && record.user_type !== 'virtual' && (
            <Can
              no={() => (
                <Tooltip title="View">
                  <Icon
                    data-testid="viewBtn"
                    name="updateUserSelenium"
                    onClick={() => {
                      handleOpenModal('details', record)
                    }}
                    type="eye"
                  />
                </Tooltip>
              )}
              requiredPermission="user.put.user_id"
              yes={() => (
                <Tooltip title="Edit">
                  <Icon
                    data-testid="editBtn"
                    name="updateUserSelenium"
                    onClick={() => {
                      handleOpenModal('details', record)
                    }}
                    type="edit"
                  />
                </Tooltip>
              )}
            />
          )}
          {record.status !== 'inactive' &&
            (record.user_type === 'regular' ||
              record.user_type === 'corporate') && (
            <Can
              requiredPermission={
                record.user_type === 'regular'
                  ? 'user.delete.user_id'
                  : 'user.put.user_id_deactivate'
              }
              yes={() => (
                <Tooltip title="Deactivate and Depersonalize">
                  <Popconfirm
                    cancelText="No"
                    className="action-list-button"
                    okText="Yes"
                    onConfirm={() => handleDepersonalizeUser(record)}
                    title="Are you sure you want to deactivate and depersonalize this user?"
                  >
                    <Icon
                      data-testid={
                        record.user_type === 'regular'
                          ? 'depersonalizeUserSelenium'
                          : 'deactivateUserSelenium'
                      }
                      name={
                        record.user_type === 'regular'
                          ? 'depersonalizeUserSelenium'
                          : 'deactivateUserSelenium'
                      }
                      type="userInactivate"
                    />
                  </Popconfirm>
                </Tooltip>
              )}
            />
          )}
        </IconContainer>
      ),
      title: 'Actions',
    },
  ])

  const handleExportUsersCSV = () => {
    exportUsers(transformSearchParams(searchParams.toString(), ['created_at']))
      .then(({ blob, fileName }) => {
        createDownloadLink(blob, fileName)

        notification.success({
          description: 'Users exported',
          duration: 3,
          title: 'Success',
        })
      })
      .catch((error) => {
        handleFailure(error, 'Users export failed!')
      })
  }

  const handleDepersonalizeUser = (record) => {
    depersonalizeUser(record.id)
      .then(() =>
        handleSuccess({ description: 'User deactivated and depersonalized!' }),
      )
      .catch((error) =>
        handleFailure(error, 'User deactivation and depersonalize failed!'),
      )
  }

  const handleFormSubmit = (formData) => {
    return updateUser(formData)
      .then(() => handleSuccess({ description: 'User updated' }))
      .catch((error) => handleFailure(error, 'User update failed!'))
  }

  const handleOpenModal = (selectedModal, selectedUser) => {
    savedListParams.current = searchParams.toString()
    setModalVisibility({ [selectedModal]: true })
    setErrorMessage(null)
    setSelectedUser(selectedUser)
    setModalLoading(false)
  }

  const handleSuccess = (messageOptions) => {
    fetchUserList(searchParams.toString())
    setErrorMessage(null)
    setModalVisibility(false)

    notification.success({
      title: 'Success',
      ...messageOptions,
    })
  }

  const handleFailure = (error, message) => {
    if (error) {
      console.log(error)
    }

    const errorMessage = handleBackendError(error, message)

    setErrorMessage(errorMessage)
    setModalLoading(false)

    notification.error({
      description: errorMessage,
      title: 'Error',
    })
  }

  const handleUserConfirmation = (fields) => {
    confirmUser(fields)
      .then(() => handleSuccess({ description: 'User confirmed' }))
      .catch((error) =>
        handleFailure(error, 'User confirmation request failed!'),
      )
  }

  return (
    <PageContainer>
      <PageTitleRow title="Users">
        <Can
          name="exportUsersSelenium"
          requiredPermission="user.get.export"
          yes={() => (
            <Button
              data-testid="exportCSVBtnSelenium"
              name="exportCSVBtnSelenium"
              onClick={handleExportUsersCSV}
            >
              Export CSV
            </Button>
          )}
        />
      </PageTitleRow>

      <Row>
        <Col span={24} style={{ overflow: 'scroll' }}>
          <Table
            className="usersPageTableSelenium"
            columns={tableColumns}
            data-testid="usersTable"
            dataSource={users}
            loading={isLoading}
            onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
            pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
            rowKey="id"
            scroll={{ x: true }}
          />

          {isModalVisible.confirmation && (
            <UserConfirmationModal
              errorMessage={errorMessage}
              hideModal={() => setModalVisibility(false)}
              isModalLoading={isModalLoading}
              onConfirm={handleUserConfirmation}
              selectedUser={selectedUser}
              setModalLoading={setModalLoading}
            />
          )}

          {isModalVisible.details && (
            <UserDetailsModal
              countries={countries}
              errorMessage={errorMessage}
              hideModal={() => setModalVisibility(false)}
              isModalLoading={isModalLoading}
              onDelete={handleDepersonalizeUser}
              onSubmit={handleFormSubmit}
              selectedUser={selectedUser}
              setModalLoading={setModalLoading}
            />
          )}

          {isModalVisible.transactions && (
            <TransactionHistoryModal
              config={config}
              errorMessage={errorMessage}
              hideModal={() => {
                setModalVisibility(false)
                fetchUserList(savedListParams.current)
              }}
              selectedUser={selectedUser}
            />
          )}
        </Col>
      </Row>
    </PageContainer>
  )
}

Users.propTypes = {
  config: configPropTypes,
}

export default withConfig(Users)
